import {
  APPROVAL_STATUS_TARGETS,
  type ApprovalRequestStatusRow,
  type ApprovalStatusTarget,
} from "./approvalRequestStatus.js";
import { UUID_PATTERN } from "./csvHelpers.js";
import { parseCsvRecords, type CsvRecord } from "./parseCsvRecords.js";

export const APPROVAL_REQUEST_ID_COLUMN = "approval_request_id";
export const STATUS_COLUMN = "status";

export type InvalidApprovalRequestStatusRow = {
  line: number;
  approvalRequestId: string;
  status: string;
  reason: string;
};

export type ParsedApprovalRequestStatusCsv = {
  rows: ApprovalRequestStatusRow[];
  invalid: InvalidApprovalRequestStatusRow[];
  duplicates: string[];
};

const REQUIRED_COLUMNS = [APPROVAL_REQUEST_ID_COLUMN, STATUS_COLUMN] as const;

function isTargetStatus(value: string): value is ApprovalStatusTarget {
  return (APPROVAL_STATUS_TARGETS as readonly string[]).includes(value);
}

function classifyRows(records: CsvRecord[]): ParsedApprovalRequestStatusCsv {
  const candidates: ApprovalRequestStatusRow[] = [];
  const invalid: InvalidApprovalRequestStatusRow[] = [];

  for (const { line, raw, parseError } of records) {
    const approvalRequestId = (raw[APPROVAL_REQUEST_ID_COLUMN] ?? "").trim().toLowerCase();
    const status = (raw[STATUS_COLUMN] ?? "").trim().toUpperCase();

    if (parseError) {
      invalid.push({ line, approvalRequestId, status, reason: parseError });
      continue;
    }
    if (!UUID_PATTERN.test(approvalRequestId)) {
      invalid.push({
        line,
        approvalRequestId,
        status,
        reason: "approval_request_id is not a UUID",
      });
      continue;
    }
    if (!isTargetStatus(status)) {
      invalid.push({
        line,
        approvalRequestId,
        status,
        reason: `status must be one of ${APPROVAL_STATUS_TARGETS.join(", ")}`,
      });
      continue;
    }
    candidates.push({ approvalRequestId, status });
  }

  const occurrences = new Map<string, number>();
  const count = (id: string) => occurrences.set(id, (occurrences.get(id) ?? 0) + 1);
  for (const row of candidates) count(row.approvalRequestId);
  for (const entry of invalid) {
    if (UUID_PATTERN.test(entry.approvalRequestId)) count(entry.approvalRequestId);
  }

  const duplicates = [...occurrences.entries()]
    .filter(([, total]) => total > 1)
    .map(([approvalRequestId]) => approvalRequestId);
  const duplicateSet = new Set(duplicates);

  return {
    rows: candidates.filter(row => !duplicateSet.has(row.approvalRequestId)),
    invalid,
    duplicates,
  };
}

export async function parseApprovalRequestStatusCsv(
  input: File | string,
): Promise<ParsedApprovalRequestStatusCsv> {
  return classifyRows(await parseCsvRecords(input, REQUIRED_COLUMNS));
}
